import { useState } from "react";
import styled from "styled-components";
import { evaluateCondition } from "../../../../common/model/condition/evaluate"
import addHeaders from "../../utils/addHeader";
import ConnectControl from "./ConnectControl";
import ConnectEmptyActions from "./ConnectEmptyActions";
import ConnectInlineControl from "./ConnectInlineControl";
import resolveDisplay from "./resolveDisplay";
import useToast from "../../hooks/useToast";

// Setup Use of API
import useApi from '../../hooks/useApi';
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import buildQueryString from "../../utils/api/buildQueryString";
import { qk } from "../../utils/queryKeys";

//This allows to return the data read from the select entities
import generateSetValueObject, { resetSetValueObject } from "./generateSetValueObject";

import useTranslation from "../../hooks/useTranslation";
import useProfile from "../../hooks/useProfile";
import { useSnapshot } from "valtio";

//Setting up the Wrapper
const SelectContainer = styled.div`
    width: ${({ $width }) => $width || '100%'};
    height: ${({ $height }) => $height || 'auto'};
    margin: 0 auto;
    position: relative;
`

//Select takes an entity like 'unit' a value like {name:'meter',id:'1'} a setter and options object.
const Connect = ({
    id, entity, parentId, mainValueId,
    name,
    valueName,
    valueMap,
    setValue,
    options,
    isRed,
    modalTitle,
    placeholder,
    displayField,
    menuItemField = 'name',
    listDisplay,
    modalType = 'searchList',
    radioGroups = [],
    subObject,
    persistValue, // Added persistValue at root level
    testId,
    linkedFields,
    defaultRadioGroups,
    entityParams,
    $marginBottom,
    $width,
    $height,
    compactOnMobile,
    manualEntry,
    autoOpen,
    inputId,
    conditionalFields,
    selectButtonKey,
    nullDefaults = {},
    emptyActions,
    actionValues,
    inline = false,
    autoFocus,
}) => {
    if (!options) { options = {} }

    // Which emptyActions pill is waiting on the server, by its id.
    const [pendingAction, setPendingAction] = useState(null);
    const { showToast, ToastComponent } = useToast();

    // Server-side search, above the default page-size limit (36.09) — matches
    // useListData's own threshold (searchTerm.length >= 3) so a query short
    // enough to be noise doesn't hit the DB, and long enough to search hits the
    // entity's real searchFields across the WHOLE collection, not just whatever
    // page loaded first. Below the threshold, ConnectControl/SelectModal still
    // filter the already-loaded page locally, same as before.
    const [searchTerm, setSearchTerm] = useState('');
    const effectiveSearch = searchTerm.length >= 3 ? searchTerm : '';

    const { pState } = useProfile();
    const profile = useSnapshot(pState);

    // Sub-entity support: when `entity` is a subcollection template containing
    // ':id' (e.g. 'warehouse/:id/hall'), substitute the parent record's id so a
    // parent card can read its children. Until parentId is known the query is
    // disabled (a new, unsaved parent has no children yet).
    const needsParent = typeof entity === 'string' && entity.includes(':id');
    const resolvedEntity = needsParent && parentId ? entity.replace(':id', parentId) : entity;
    const parentReady = !needsParent || !!parentId;

    let queryString = null;

    //If entityParams is defined, build the query string to get back the right data
    if (entityParams) {
        queryString = buildQueryString(entityParams, linkedFields);
    }

    //console.log('connect Linked Fields: ', linkedFields) 
    if (typeof subObject === 'string' && subObject.trim() !== '' && testId) {
        const parts = testId.split('.');
        const index = parts.lastIndexOf('locationId');
        if (index !== -1) {
            parts.splice(index, 0, subObject);
            testId = parts.join('.');
        }
    }

    //Setting up API
    const { getMe, postMe } = useApi();
    const { t, m } = useTranslation();

    // Pre-selection for radioGroup pickers: seed a group (e.g. 'warehouse') from a
    // linked form field. `defaultRadioGroups` maps groupId → one or more linked
    // field names; the first non-empty linked value wins. Used to default the
    // location picker's warehouse to the order's origin/destination (Phase C).
    const defaultSelection = (() => {
        if (!defaultRadioGroups || !linkedFields) return undefined;
        const out = {};
        for (const [groupId, fields] of Object.entries(defaultRadioGroups)) {
            const names = Array.isArray(fields) ? fields : [fields];
            for (const n of names) {
                if (linkedFields[n]) { out[groupId] = linkedFields[n]; break; }
            }
        }
        return Object.keys(out).length ? out : undefined;
    })();

    //Api Retrieval function to get list for the Entity
    const getData = async () => {
        let result
        let resource = queryString ? `${resolvedEntity}${queryString}` : `${resolvedEntity}`
        if (effectiveSearch) {
            resource += (queryString ? '&' : '?') + `searchString=${encodeURIComponent(effectiveSearch)}`
        }
        const response = await getMe(resource)
        if (response.data === undefined) {
            result = addHeaders([{ name: t('no.results.found'), unselectable: true }], false)
        } else {
            //This adds the headers if options.withHeaders is true
            result = addHeaders(response.data, options.withHeaders)
        }

        return result
    }

    //Return Data to Form
    const returnData = (data, valueMap) => {
        let effectiveValueMap = valueMap;
        if (conditionalFields) {
            const { fields, condition } = conditionalFields;
            // `valueFrom: 'profile.x'` replaces the old `equalsProfile: 'x'`.
            // Still requires a truthy linked value, as before.
            const conditionMet = !!linkedFields?.[condition.field]
                && evaluateCondition(condition, { values: linkedFields, profile });
            if (!conditionMet) {
                effectiveValueMap = Object.fromEntries(
                    Object.entries(valueMap).filter(([k]) => !fields.includes(k))
                );
            }
        }
        const result = generateSetValueObject(data, effectiveValueMap, nullDefaults);

        // listDisplay drives the picker's list rows (SelectModal) and, when set,
        // this too — so a field configured to show "name - number" shows that
        // same text once picked, not just the raw name valueMap copied over.
        if (listDisplay && displayField) {
            const displayValue = resolveDisplay(data, menuItemField, listDisplay);
            const existing = result.find(r => r.target.name === displayField);
            if (existing) {
                existing.target.value = displayValue;
            } else {
                result.push({ target: { name: displayField, value: displayValue } });
            }
        }

        setValue(result);
    }

    /*
     * An emptyActions pill: ask its server-side resolver what the field should
     * hold, and take the answer as if the user had picked it from the list — the
     * same valueMap, the same display. Only the form changes; the record is
     * stored by the user's own save. `params` names the form values sent along,
     * read from `actionValues`, which FormElem fills from the field's scope.
     */
    const runEmptyAction = async (action) => {
        if (pendingAction) return;
        setPendingAction(action.id);
        try {
            const params = Object.fromEntries((action.params || []).map(p => [p, actionValues?.[p] ?? null]));
            const response = await postMe(`resolve/${action.resolver}`, { params });
            if (response?.data) {
                returnData(response.data, valueMap);
                if (response.message) showToast(t(action.label), m(response.message, response.messageParams), 'success');
            } else {
                showToast(t(action.label), t(response?.message || response?.error || 'resolver.error.failed'), 'warning');
            }
        } finally {
            setPendingAction(null);
        }
    }

    //Clear the field
    const clearField = () => {
        const result = resetSetValueObject(valueMap);
        setValue(result);
    }

    //Handle manual text entry — only sets the display field, leaves all other fields untouched
    const handleManualEntry = (text) => {
        const displayKey = Object.keys(valueMap).find(key => valueMap[key] === menuItemField);
        if (!displayKey) return;
        setValue([{ target: { name: displayKey, value: text } }]);
    }

    //Request to get Countries from API
    const result = useQuery({
        queryKey: qk.connect(resolvedEntity, queryString, effectiveSearch),
        queryFn: getData,
        // The last list stays up while a new search term loads, instead of
        // the picker emptying on every keystroke past the third.
        placeholderData: keepPreviousData,
        enabled: parentReady
    })

    if (result.isError) {
        return <span>Error: {result.error ? result.error.message : 'Internet Issues'}</span>
    }

    // `inline`: searched where it stands, the matches beneath it — no modal.
    if (inline) {
        return (
            <SelectContainer $height={$height} $width={$width}>
                <ConnectInlineControl
                    data={result.data}
                    stale={result.isPlaceholderData}
                    valueName={valueName}
                    onSelect={selection => returnData(selection, valueMap)}
                    onSearch={setSearchTerm}
                    placeholder={placeholder}
                    options={options}
                    menuItemField={menuItemField}
                    listDisplay={listDisplay}
                    isRed={isRed}
                    autoFocus={autoFocus}
                    testId={testId}
                    inputId={inputId}
                    name={name}
                />
            </SelectContainer>
        )
    }

    return (
        <div style={$height ? { display: 'flex', width: '100%', height: $height } : {}}>
            <SelectContainer id='SearchBarContainer' $height={$height} $width={$width}>
                <ConnectControl
                    id={mainValueId}
                    name={name}
                    mainValueId={mainValueId}
                    valueName={valueName}
                    setValue={setValue}
                    data={result.data}
                    placeholder={placeholder}
                    onSelect={selection => returnData(selection, valueMap)}
                    entity={resolvedEntity}
                    modalTitle={modalTitle}
                    options={options}
                    isRed={isRed}
                    $alwaysOn={true}
                    menuItemField={menuItemField}
                    listDisplay={listDisplay}
                    modalType={modalType}
                    radioGroups={radioGroups}
                    defaultSelection={defaultSelection}
                    persistValue={persistValue}
                    testId={testId}
                    subObject={subObject}
                    onClear={clearField}
                    onSearch={setSearchTerm}
                    $marginBottom={$marginBottom}
                    $width={$width}
                    $height={$height}
                    compactOnMobile={compactOnMobile}
                    manualEntry={manualEntry}
                    onManualEntry={handleManualEntry}
                    autoOpen={autoOpen}
                    inputId={inputId}
                    selectButtonKey={selectButtonKey}
                    emptyAdornment={emptyActions?.length ? (
                        <ConnectEmptyActions actions={emptyActions} pending={pendingAction}
                            onRun={runEmptyAction} t={t} testId={testId} />
                    ) : null}
                />
            </SelectContainer>
            {ToastComponent}
        </div>
    )
};

export default Connect;
